<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageTooltip from '@/components/common/BackstageTooltip.vue';
import PlusIcon from '@/components/common/plus-icon.vue';
import { Icons } from '@/models/icons.enum';
import { FeaturesService } from '@/services/features.service';
import type { AllowableProductModelDto } from '@onrunning/backstage-models';
import { computed } from 'vue';
import BackstageImageV2 from '../common/BackstageImageV2.vue';

const props = defineProps<{
  productModel: AllowableProductModelDto;
  isSelected: boolean;
}>();

const emit = defineEmits<{
  showProductModelDetailsPopup: [];
}>();

const showProductModelDetailsPopup = () => {
  emit('showProductModelDetailsPopup');
};

const productImage = computed(() => {
  return props.productModel.defaultImage;
});

const showMcaBadge = computed(() => FeaturesService.mcaEnabled && !!props.productModel.isMca);
</script>

<template>
  <article class="product-model-card" v-bind="$attrs" :class="[{ selected: isSelected }]">
    <BackstageTooltip v-if="showMcaBadge" :text="$t('matrix.mca_tooltip_title')" class="mca-badge">
      <BackstageIcon
        :name="Icons.RECOMMENDED"
        class="mca-icon"
        :aria-label="$t('matrix.mca_tooltip_title')"
        aria-hidden="true" />
    </BackstageTooltip>
    <div class="card-top" data-cy="card-top">
      <BackstageImageV2 class="img" :src="productImage" />
    </div>
    <div class="card-bottom" data-cy="card-bottom">
      <span>{{ productModel.name }}</span>
      <PlusIcon :selected="isSelected"></PlusIcon>
    </div>
    <div
      v-if="productImage && productModel.defaultVariantImages?.length"
      class="expand-image-btn"
      data-cy="image-expand-button"
      @click="showProductModelDetailsPopup"></div>
  </article>
</template>

<style scoped lang="scss">
.product-model-card {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: space-around;
  width: 100%;
  cursor: pointer;
  border-radius: 4px;
  box-sizing: border-box;
  aspect-ratio: 172.29 / 190;
  color: var(--primary-text-color);
  background-color: var(--primary-bg-color);

  &.selected {
    outline: 2px solid var(--new-primary-color);
  }

  @media (hover) {
    &:focus {
      border: 2px solid var(--bg-remark-selected-color);
    }

    &:hover {
      background-color: var(--bg-remark-selected-color);
    }

    &:hover,
    &:focus {
      .expand-image-btn {
        display: block;
      }
    }
  }

  .mca-badge {
    position: absolute;
    top: 2px;
    left: 2px;
    width: fit-content;
    line-height: 0;
    z-index: 1;

    .mca-icon {
      width: 24px;
      height: 24px;
    }
  }

  .card-top {
    width: 100%;
    height: 70%;

    .img {
      height: 100%;
      width: 100%;
      object-fit: contain;
    }
  }

  .card-bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-right: 1rem;
    padding-left: 1rem;
    width: 100%;
    gap: 1rem;

    span {
      text-align: left;
      font-size: 0.875em;
    }
  }

  .expand-image-btn {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 24px;
    height: 24px;
    @include icon-image('@/assets/img/icons/expand.svg', var(--primary-text-color));
    @media (hover) {
      display: none;
    }
  }
}

.dark
  .products-models-selector
  .products-models-selector-cnt
  .products-models-list
  .product-model-cards
  article.product-model-card {
  background-color: var(--bg-remark-selected-color);
}
</style>
